import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminPanel.css";
import AdminSidebar from "./AdminSidebar";
import AdministratorSidebar from "./AdministratorSidebar";
import UserSidebar from "./UserSidebar";

const ViewPayments = () => {
    const navigate = useNavigate();
    const [userType, setUserType] = useState("");
    const [userName, setUserName] = useState("");
    const [userFullName, setUserFullName] = useState("");
    const [soldItems, setSoldItems] = useState([]);
    const [selectedStockId, setSelectedStockId] = useState("");
    const [payments, setPayments] = useState([]);
    const [price, setPrice] = useState(null);
    const [newPayment, setNewPayment] = useState({
        amount: "",
        payment_date: "",
        payment_method: "Cash",
        notes: ""
    });
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const storedUserType = localStorage.getItem("usertype");
        const storedUserName = localStorage.getItem("userName");
        const storedFName = localStorage.getItem("fname");
        const storedLName = localStorage.getItem("lname");

        if (!storedUserType) {
            navigate("/login");
        } else {
            setUserType(storedUserType);
            setUserName(storedUserName);
            if (storedFName && storedLName) {
                setUserFullName(`${storedFName} ${storedLName}`);
            } else if (storedFName) {
                setUserFullName(storedFName);
            } else {
                setUserFullName(storedUserName);
            }
            fetchSoldItems();
        }
    }, [navigate]);

    const fetchSoldItems = async () => {
        try {
            const response = await fetch("https://api.mitradingjp.com/getSoldCars.php");
            const data = await response.json();

            if (data.success) {
                setSoldItems(data.cars);
                if (data.cars.length > 0) {
                    setSelectedStockId(data.cars[0].stock_id);
                    fetchPayments(data.cars[0].stock_id);
                }
            } else {
                setMessage(data.message);
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching sold items:", error);
            setMessage("Error fetching sold items");
            setLoading(false);
        }
    };

    const fetchPayments = async (stockId) => {
        if (!stockId) return;

        try {
            const response = await fetch(`https://api.mitradingjp.com/getPayments.php?stockId=${stockId}`);
            const data = await response.json();

            if (data.success) {
                setPayments(data.payments);
                setPrice(parseFloat(data.price) || 0);
                setMessage("");
            } else {
                setPayments([]);
                setPrice(null);
                setMessage(data.message);
            }
        } catch (error) {
            console.error("Error fetching payments:", error);
            setMessage("Error fetching payments");
        }
    };

    const handleStockIdChange = (e) => {
        const stockId = e.target.value;
        setSelectedStockId(stockId);
        fetchPayments(stockId);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewPayment(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddPayment = async (e) => {
        e.preventDefault();

        if (!selectedStockId) {
            setMessage("Please select a sold item first");
            return;
        }

        const formData = new FormData();
        formData.append("stockId", selectedStockId);
        Object.entries(newPayment).forEach(([key, value]) => {
            formData.append(key, value);
        });

        try {
            const response = await fetch("https://api.mitradingjp.com/addPayment.php", {
                method: "POST",
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                setMessage("Payment added successfully!");
                fetchPayments(selectedStockId);
                setNewPayment({
                    amount: "",
                    payment_date: "",
                    payment_method: "Cash",
                    notes: ""
                });
            } else {
                setMessage(data.message || "Error adding payment");
            }
        } catch (error) {
            console.error("Error adding payment:", error);
            setMessage("Error adding payment");
        }
    };

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("email");
        localStorage.removeItem("userName");
        localStorage.removeItem("usertype");
        localStorage.removeItem("fname");
        localStorage.removeItem("lname");
        navigate("/login");
    };

    // Calculate total payments and remaining balance
    const totalPayments = payments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
    const remainingBalance = price !== null ? (price - totalPayments).toFixed(2) : "N/A";

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="admin-container">
            <div className="sidebar-container">
                {userType === "administrator" && <AdministratorSidebar />}
                {userType === "admin" && <AdminSidebar />}
                {userType === "user" && <UserSidebar />}
            </div>

            <div className="admin-content">
                <div className="adminnavbar">
                    <h1>Welcome to the Admin Panel, {userFullName}</h1>
                    <button onClick={logout} className="logoutbtn">
                        Logout
                    </button>
                </div>

                <div className="mainsection">
                    <h3>View and Add Payments</h3>

                    <div className="p-4 border rounded bg-light">
                        {message && (
                            <div className={`alert ${message.includes("success") ? "alert-success" : "alert-danger"}`}>
                                {message}
                            </div>
                        )}

                        <div className="mb-4">
                            <label htmlFor="stockId" className="form-label fw-bold">Select Sold Item:</label>
                            <select
                                id="stockId"
                                className="form-select"
                                value={selectedStockId}
                                onChange={handleStockIdChange}
                                disabled={soldItems.length === 0}
                            >
                                {soldItems.length > 0 ? (
                                    soldItems.map((item) => (
                                        <option key={item.stock_id} value={item.stock_id}>
                                            {item.stock_id} - {item.client_name}
                                        </option>
                                    ))
                                ) : (
                                    <option value="">No sold items found</option>
                                )}
                            </select>
                        </div>

                        {/* Payment Summary */}
                        {selectedStockId && price !== null && (
                            <div className="card p-4 mb-4">
                                <h5 className="mb-3">Payment Summary for {selectedStockId}</h5>
                                <div className="row">
                                    <div className="col-md-4">
                                        <p><strong>Total Price:</strong> ${price.toFixed(2)}</p>
                                    </div>
                                    <div className="col-md-4">
                                        <p><strong>Total Paid:</strong> ${totalPayments.toFixed(2)}</p>
                                    </div>
                                    <div className="col-md-4">
                                        <p><strong>Remaining Balance:</strong> ${remainingBalance}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Payment Form */}
                        <div className="card p-4 mb-4">
                            <h5 className="mb-3">Add New Payment</h5>
                            <form onSubmit={handleAddPayment}>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Amount</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="amount"
                                            value={newPayment.amount}
                                            onChange={handleInputChange}
                                            required
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Payment Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="payment_date"
                                            value={newPayment.payment_date}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Payment Method</label>
                                        <select
                                            className="form-select"
                                            name="payment_method"
                                            value={newPayment.payment_method}
                                            onChange={handleInputChange}
                                        >
                                            <option value="Cash">Cash</option>
                                            <option value="Bank Transfer">Bank Transfer</option>
                                            <option value="Credit Card">Credit Card</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Notes (Optional)</label>
                                        <textarea
                                            className="form-control"
                                            name="notes"
                                            value={newPayment.notes}
                                            onChange={handleInputChange}
                                            rows="2"
                                        />
                                    </div>
                                </div>
                                <div className="mt-4 text-end">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add Payment
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Payments List */}
                        <div className="card p-4">
                            <h5 className="mb-3">Payment History</h5>
                            {payments.length > 0 ? (
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Amount</th>
                                            <th>Date</th>
                                            <th>Method</th>
                                            <th>Notes</th>
                                            <th>Created At</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map((payment) => (
                                            <tr key={payment.id}>
                                                <td>${parseFloat(payment.amount).toFixed(2)}</td>
                                                <td>{payment.payment_date}</td>
                                                <td>{payment.payment_method}</td>
                                                <td>{payment.notes || "-"}</td>
                                                <td>{new Date(payment.created_at).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-muted">No payments found for this stock ID.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewPayments;